import moment from 'moment';

type ScoreInterval = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

export type ScoreTimeRange = {
	current: [Date, Date];
	interval: ScoreInterval;
};

const SERIES_POINTS = 12;

export const SCORE_INTERVAL: Record<
	ScoreInterval,
	{ unit: moment.unitOfTime.DurationConstructor; format: string }
> = {
	day: { unit: 'day', format: 'D MMM' },
	week: { unit: 'week', format: 'D MMM' },
	month: { unit: 'week', format: 'D MMM' },
	quarter: { unit: 'month', format: 'MMM' },
	year: { unit: 'month', format: 'MMM YY' },
	custom: { unit: 'day', format: 'D MMM' },
};

const seriesPointCount = (interval: ScoreInterval): number => {
	if (interval === 'month') return 5;
	if (interval === 'quarter') return 3;
	return SERIES_POINTS;
};

// A period that still runs into the future (e.g. October on the 8th) is scored
// as of now. Evaluating at month-end would treat the remaining empty days as
// missed 1:1s / feedback and pull the score down.
export const clampScoreEndDate = (end: Date, now: Date = new Date()): Date =>
	end.getTime() > now.getTime() ? new Date(now.getTime()) : end;

// History end-dates for the active filter: a trailing run of `SERIES_POINTS`
// period ends counting back from the selected period, with the final point
// pinned to the (possibly clamped) end so the last chart point matches the
// headline. A custom range is sampled evenly between its bounds instead.
export const buildSeriesEndDates = (
	timeMachine: ScoreTimeRange,
	workspaceStartDate?: Date,
	now: Date = new Date(),
): Date[] => {
	const [start, selectedEnd] = timeMachine.current;
	const end = clampScoreEndDate(selectedEnd, now);

	const endDates =
		timeMachine.interval === 'custom'
			? buildCustomEndDates(start, end, now)
			: buildTrailingEndDates(timeMachine.interval, end);

	const unique = uniqueEndDates(endDates);

	if (!workspaceStartDate) return unique;

	const trimmed = unique.filter((date) => date >= workspaceStartDate);

	return trimmed.length > 0 ? trimmed : [unique[unique.length - 1]];
};

const uniqueEndDates = (dates: Date[]): Date[] => {
	const seen = new Set<number>();
	const result: Date[] = [];

	for (const date of dates) {
		const time = date.getTime();
		if (seen.has(time)) continue;
		seen.add(time);
		result.push(date);
	}

	return result;
};

const buildTrailingEndDates = (interval: ScoreInterval, end: Date): Date[] => {
	const { unit } = SCORE_INTERVAL[interval];
	const last = moment(end);
	const points = seriesPointCount(interval);

	return Array.from({ length: points }, (_, index) => {
		const stepsBack = points - 1 - index;

		if (stepsBack === 0) return last.toDate();

		return last.clone().subtract(stepsBack, unit).endOf(unit).toDate();
	});
};

const buildCustomEndDates = (start: Date, end: Date, now: Date): Date[] => {
	const from = moment(start);
	const to = moment(end);
	const span = Math.max(to.diff(from, 'days'), 1);
	const step = span / (SERIES_POINTS - 1);

	return Array.from({ length: SERIES_POINTS }, (_, index) => {
		const date = from.clone().add(Math.round(step * index), 'days');
		const point = (index === SERIES_POINTS - 1 ? to : date).toDate();

		return clampScoreEndDate(point, now);
	});
};
